import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { FilingFormIdb } from './persistence/filing-form-idb';
import type { FilingFormState } from './filing-form-state.service';

/** Legacy localStorage key prefixes for the per-conversion filing state (category A). */
const PREFIXES = {
  original: 'mbrs-filing-form-original-',
  modified: 'mbrs-filing-form-modified-',
  metadata: 'mbrs-filing-form-metadata-',
  currentSheet: 'mbrs-current-sheet-',
} as const;

/**
 * A queued write. Three modes, and the distinction is load-bearing:
 *
 *   replace — write this exact value, discarding whatever is stored. What every
 *             whole-document caller wants (an API load, a revert, a batch flush).
 *   merge   — write only these top-level keys, preserving the rest of what is
 *             stored AT DRAIN TIME. This is what makes a per-sheet edit safe
 *             when another tab has written a different sheet in the meantime.
 *   delete  — remove the key.
 */
type PendingValue =
  | { kind: 'replace'; value: unknown }
  | { kind: 'merge'; patch: Record<string, unknown> }
  | { kind: 'delete' };

/**
 * Owns IndexedDB persistence for the per-conversion filing-form dual state.
 *
 * Writes are synchronous-return / fire-and-forget: callers (the form-edit
 * pipeline) never await. A per-key queue coalesces a burst to the last value
 * and guarantees in-order, last-write-wins landing. Reads are async and used
 * only at hydration / cross-tab / submit / headless-validator boundaries.
 *
 * Falls back to an in-memory Map when IndexedDB is unavailable (private mode),
 * so the app runs without persistence rather than throwing.
 */
@Injectable({ providedIn: 'root' })
export class FilingFormPersistenceService {
  private readonly idb = new FilingFormIdb();

  /**
   * Emits when a persistence write hard-fails. This service is `providedIn:
   * 'root'`, so it cannot inject PrimeNG's component-scoped `MessageService`
   * (doing so crashed the app — there is no root provider). Instead it emits
   * here and an always-mounted component with a toast (MainLayoutComponent)
   * surfaces it. Keeps error-surfacing without a root-scope DI dependency.
   */
  readonly writeErrors$ = new Subject<{ summary: string; detail: string }>();

  /** Resolves to true if IDB is usable; false -> in-memory fallback. */
  private readonly availability: Promise<boolean> = this.idb.isAvailable();
  private readonly memory = new Map<string, unknown>();

  /** Per-key coalescing queue: latest desired value + an in-flight drain promise. */
  private readonly pending = new Map<string, PendingValue>();
  private readonly draining = new Map<string, Promise<void>>();

  constructor() {
    this.purgeLegacyLocalStorage();
  }

  // ---- key builders ----
  private kOriginal(id: string) { return `${PREFIXES.original}${id || 'new'}`; }
  private kModified(id: string) { return `${PREFIXES.modified}${id || 'new'}`; }
  private kMetadata(id: string) { return `${PREFIXES.metadata}${id || 'new'}`; }
  private kCurrentSheet(id: string) { return `${PREFIXES.currentSheet}${id || 'new'}`; }

  // ---- reads ----
  /**
   * @param opts.bypassPending skip this tab's own coalescing queue and read what
   *   is actually persisted. Used ONLY by the cross-tab remote rehydrate, where a
   *   queued LOCAL write is by definition not the sibling's update we were told
   *   to apply. Every other caller wants the queue (it is the freshest truth for
   *   this tab).
   */
  getOriginal(id: string, opts?: { bypassPending?: boolean }): Promise<FilingFormState | null> { return this.read(this.kOriginal(id), opts); }
  getModified(id: string, opts?: { bypassPending?: boolean }): Promise<FilingFormState | null> { return this.read(this.kModified(id), opts); }
  getMetadata(id: string): Promise<Record<string, unknown> | null> { return this.read<Record<string, unknown>>(this.kMetadata(id)); }
  getCurrentSheet(id: string): Promise<string | null> { return this.read<string>(this.kCurrentSheet(id)); }

  private async read<T>(key: string, opts?: { bypassPending?: boolean }): Promise<T | null> {
    // NOTE: callers at submit/hydration/cross-tab boundaries must call flush()
    // first if they require IDB to reflect the very latest queued write. There
    // is a brief window after a drain dequeues an item but before idb.set()
    // resolves where a read falls through to the prior persisted value.
    //
    // A queued write for this key is the freshest truth for THIS tab — return it,
    // unless the caller explicitly wants to bypass it (see getOriginal/getModified doc).
    if (!opts?.bypassPending) {
      const queued = this.pending.get(key);
      if (queued) {
        if (queued.kind === 'delete') return null;
        if (queued.kind === 'replace') return queued.value as T;
        // A queued MERGE is only a patch, so it is not the whole truth on its
        // own — the freshest view for this tab is the stored value with the
        // patch laid over it. Fall through to the store, then apply. (Read
        // recursively with bypassPending so we get the stored base, not this
        // same queued entry again.)
        const base = await this.read<Record<string, unknown>>(key, { bypassPending: true });
        return { ...(base ?? {}), ...queued.patch } as T;
      }
    }

    if (!(await this.availability)) {
      return this.memory.has(key) ? (this.memory.get(key) as T) : null;
    }

    try {
      return await this.idb.get<T>(key);
    }
    catch (err) {
      console.error('[FilingFormPersistence] read failed', key, err);
      return null;
    }
  }

  // ---- writes (fire-and-forget) ----
  putOriginal(id: string, state: FilingFormState): void { this.enqueue(this.kOriginal(id), { kind: 'replace', value: state }); }
  /**
   * Persist the modified state.
   *
   * `changedKeys` present → persist ONLY those sheet slices, merged against the
   * live store at drain time inside a single IndexedDB readwrite transaction, so
   * a slice another TAB wrote while this write was queued survives. Absent →
   * replace the whole document, which is what the bulk callers (an API load, a
   * revert, a batch flush) intend.
   *
   * Why the per-sheet path exists: a whole-document write rewrites every sheet
   * from one tab's in-memory view, last-write-wins. A tab editing Filing
   * Information was therefore reverting a sibling's newer save on a sheet it had
   * never opened — reproduced as real data loss (999 overwritten back to 100),
   * not merely a mislit "edited" icon.
   *
   * Deliberately an OPTIONAL PARAMETER rather than a new method: this service is
   * hand-stubbed in many specs across the suite, and adding a method breaks every
   * one of those stubs at runtime, while an extra argument is ignored harmlessly
   * by an existing stub. Learned the hard way — a new method turned 3037 green
   * specs into 80 failures.
   */
  putModified(id: string, state: FilingFormState, changedKeys?: (keyof FilingFormState)[]): void {
    if (changedKeys && changedKeys.length > 0) {
      const patch: Record<string, unknown> = {};
      for (const key of changedKeys)
        patch[key as string] = (state as any)[key];
      this.enqueue(this.kModified(id), { kind: 'merge', patch });
      return;
    }
    this.enqueue(this.kModified(id), { kind: 'replace', value: state });
  }
  putMetadata(id: string, meta: any): void { this.enqueue(this.kMetadata(id), { kind: 'replace', value: meta }); }
  putCurrentSheet(id: string, sheet: string): void { this.enqueue(this.kCurrentSheet(id), { kind: 'replace', value: sheet }); }

  /**
   * Coalesce a write into the per-key queue.
   *
   * The coalescing rules preserve intent across a burst:
   *   - a `replace` supersedes anything queued (it means "this is the whole truth");
   *   - a `merge` onto a queued `replace` folds into that replace, so the
   *     replace stays authoritative and the patch is not lost;
   *   - a `merge` onto a queued `merge` combines the patches in order;
   *   - a `delete` supersedes everything.
   *
   * Getting this wrong is silent: the queue would simply drop one of two writes
   * that arrived in the same debounce window.
   */
  private enqueue(key: string, next: PendingValue): void {
    const queued = this.pending.get(key);

    if (next.kind === 'merge' && queued) {
      if (queued.kind === 'replace') {
        this.pending.set(key, {
          kind: 'replace',
          value: { ...(queued.value as Record<string, unknown>), ...next.patch },
        });
      }
      else if (queued.kind === 'merge') {
        this.pending.set(key, { kind: 'merge', patch: { ...queued.patch, ...next.patch } });
      }
      else {
        // A delete is queued; the merge would resurrect a key the caller asked
        // to remove. Replace semantics are the safer reading here.
        this.pending.set(key, { kind: 'replace', value: { ...next.patch } });
      }
    }
    else {
      this.pending.set(key, next);
    }

    if (!this.draining.has(key)) {
      this.draining.set(key, this.drain(key));
    }
  }

  /** Drain a single key's queue: write the latest value until no newer one arrives. */
  private async drain(key: string): Promise<void> {
    try {
      const available = await this.availability;
      while (this.pending.has(key)) {
        const next = this.pending.get(key)!;
        
        this.pending.delete(key);
        
        try {
          if (next.kind === 'delete') {
            available ? await this.idb.delete(key) : this.memory.delete(key);
          }
          else if (next.kind === 'merge') {
            // Read-modify-write against the LIVE store, so a slice another tab
            // wrote while this one was queued survives. `idb.update` does both
            // halves inside one readwrite transaction, which is what makes this
            // atomic across tabs rather than merely across this tab's queue.
            if (available) {
              await this.idb.update<Record<string, unknown>>(
                key,
                (current) => ({ ...(current ?? {}), ...next.patch })
              );
            }
            else {
              const current = (this.memory.get(key) as Record<string, unknown> | undefined) ?? {};
              this.memory.set(key, { ...current, ...next.patch });
            }
          }
          else if (available) {
            await this.idb.set(key, next.value);
          }
          else {
            this.memory.set(key, next.value);
          }
        }
        catch (err) {
          console.error('[FilingFormPersistence] write failed', key, err);

          this.writeErrors$.next({
            summary: 'Save failed',
            detail: "Your changes couldn't be saved locally. Please copy any critical edits and reload.",
          });
        }
      }
    }
    finally {
      this.draining.delete(key);
      
      // A value enqueued between the loop exit and delete() restarts the drain.
      if (this.pending.has(key) && !this.draining.has(key)) {
        this.draining.set(key, this.drain(key));
      }
    }
  }

  /** Await all in-flight writes (tests + visibilitychange flush). */
  async flush(): Promise<void> {
    await Promise.all(Array.from(this.draining.values()));
  }

  // ---- teardown ----
  async remove(id: string): Promise<void> {
    const keys = [this.kOriginal(id), this.kModified(id), this.kMetadata(id), this.kCurrentSheet(id)];
    
    // Drop queued writes for these keys and let any in-flight drains settle
    // before deleting, so a late write cannot resurrect a removed conversion.
    keys.forEach(key => this.pending.delete(key));
    
    const inFlight = keys.map(key => this.draining.get(key)).filter(Boolean) as Promise<void>[];
    
    if (inFlight.length)
      await Promise.all(inFlight);

    if (!(await this.availability)) {
      keys.forEach(key => this.memory.delete(key));
      return;
    }
    
    await Promise.all(keys.map(key => this.idb.delete(key).catch(() => void 0)));
  }

  /** Remove just the current-sheet pointer for one conversion. */
  async removeCurrentSheet(id: string): Promise<void> {
    const key = this.kCurrentSheet(id);
    this.pending.delete(key);
    
    const inFlight = this.draining.get(key);
    
    if (inFlight)
      await inFlight;

    if (!(await this.availability)) {
      this.memory.delete(key); return;
    }
    await this.idb.delete(key).catch(() => void 0);
  }

  /** Remove every current-sheet pointer (all conversions). Leaves form blobs intact. */
  async clearAllCurrentSheets(): Promise<void> {
    await this.clearByPrefix(PREFIXES.currentSheet);
  }

  /**
   * Remove every form-state blob (original/modified/metadata, all conversions).
   * Mirrors the old localStorage clearAllPersistedFormState scope — does NOT
   * touch the current-sheet pointer.
   */
  async clearAllFormState(): Promise<void> {
    await this.clearByPrefix(PREFIXES.original, PREFIXES.modified, PREFIXES.metadata);
  }

  /** Shared prefix-scoped delete across IDB or the memory fallback. */
  private async clearByPrefix(...prefixes: string[]): Promise<void> {
    const matches = (key: string) => prefixes.some(prefix => key.startsWith(prefix));
    // Drop queued writes for matching keys, then let in-flight drains settle.
    Array.from(this.pending.keys()).filter(matches).forEach(key => this.pending.delete(key));
    await Promise.all(Array.from(this.draining.values()));
    if (!(await this.availability)) {
      Array.from(this.memory.keys()).filter(matches).forEach(key => this.memory.delete(key));
      return;
    }
    const keys = (await this.idb.keys()).filter(matches);
    await Promise.all(keys.map(key => this.idb.delete(key).catch(() => void 0)));
  }

  async clearAll(): Promise<void> {
    this.pending.clear();
    // Await any in-flight drains so a late idb.set() cannot resurrect a value
    // after the store is cleared.
    await Promise.all(Array.from(this.draining.values()));

    if (!(await this.availability)) {
      this.memory.clear();
      return;
    }

    try {
      await this.idb.clear();
    }
    catch (err) {
      console.error('[FilingFormPersistence] clearAll failed', err);
    }
  }

  /**
   * One-time blind purge of legacy category-A localStorage keys. Idempotent —
   * runs at construction (and safe to call again). Leaves sentinels (`__mbrs-*`)
   * and unrelated (`yyc-*`) keys untouched. Reclaims the quota the old bloated
   * blobs squat on; pre-deploy edits living only in localStorage are abandoned
   * by design (see spec).
   */
  purgeLegacyLocalStorage(): void {
    if (typeof window === 'undefined')
      return;
    
    const legacy = Object.values(PREFIXES);
    
    try {
      const toRemove: string[] = [];
      for (let storageIndex = 0; storageIndex < localStorage.length; storageIndex++) {
        const key = localStorage.key(storageIndex);
        if (key && legacy.some(prefix => key.startsWith(prefix))) toRemove.push(key);
      }
      toRemove.forEach(key => localStorage.removeItem(key));
    }
    catch {
      // private-mode browsers may refuse; ignore.
    }
  }
}
